import { Grid } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import FormHelper from "../../../../helpers/FormHelper";
import SelectField from "../../../../components/SelectField";
import { useUserContext } from "../../../../contexts/UserContext";
import { IRoomBlocking } from "../../../../interfaces/IPartnerPms";
import DateTimeHelper from "../../../../helpers/DateTimeHelper";
import PartnerPmsService from "../../../../services/PartnerPmsService";
import DateField from "../../../../components/DateField";


const AddEditDialog: React.FunctionComponent<IAddEditDialogProps<IRoomBlocking>> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {}
}) => {
    const { translate } = useLanguageContext();
    const { enterprise } = useUserContext();

    const [roomId, setRoomId] = useState<number | undefined>(undefined);
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [typeId, setTypeId] = useState<number | undefined>(undefined);
    const [description, setDescription] = useState<string>('');


    useEffect(() => {
        if (open) {
            if (data) {
                const date = DateTimeHelper.ToDate(data.date);

                setRoomId(data.roomId);
                setStartDate(date);
                setEndDate(date);
                setTypeId(data.typeId);
                setDescription(data.description);
            } else {
                const now = DateTimeHelper.ToDate(undefined);

                setRoomId(undefined);
                setStartDate(now);
                setEndDate(now);
                setTypeId(undefined);
                setDescription('');
            }
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        PartnerPmsService.AddRoomBlocking(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);


    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='sm'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={4} xs={12}>
                    <SelectField
                        label={translate('room')}
                        name='roomId'
                        value={roomId}
                        onChange={e => setRoomId(e.target.value)}
                        data={(enterprise?.partnerPms.rooms ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        })}
                    />
                </Grid>

                <Grid item md={8} xs={12}>
                    <SelectField
                        label={translate('blockingType')}
                        name='typeId'
                        value={typeId}
                        onChange={e => setTypeId(e.target.value)}
                        data={(enterprise?.partnerPms.roomBlockingTypes ?? []).map(r => {
                            return {
                                value: +r.id,
                                text: r.description
                            };
                        })}
                    />
                </Grid>
                
                <Grid item md={6} xs={12}>
                    <DateField
                        label={translate('startDate')}
                        format='DD/MM/YYYY'
                        name='startDate'
                        value={startDate}
                        onChange={value => setStartDate(value)}
                        disablePast={true}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <DateField
                        label={translate('endDate')}
                        format='DD/MM/YYYY'
                        name='endDate'
                        value={endDate}
                        onChange={value => setEndDate(value)}
                        disablePast={true}
                    />
                </Grid>

                <Grid item xs={12}>
                    <TextField 
                        label={translate('description')} 
                        name='description' 
                        value={description} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 100, setDescription)}
                        multiline={true}
                        minRows={6}
                        maxRows={6}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;